import React, { useState } from "react";
import api, { API } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { UploadCloud, Server, CheckCircle2, AlertTriangle, FileText, Building2, ShieldAlert, Download, Wand2, ListChecks, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import { fmtINR } from "@/lib/format";
import VoucherTypeMap from "@/components/migrator/VoucherTypeMap";

const CATEGORIES = [
  { v: "gl", label: "GL Account" },
  { v: "ar", label: "AR (Customer)" },
  { v: "ap", label: "AP (Vendor)" },
  { v: "fixed_asset", label: "Fixed Asset" },
  { v: "skip", label: "Skip" },
];

// File-size thresholds (bytes). The user's own testing confirmed ~15 MB
// files upload reliably; larger exports risk K8s pod OOM during parse.
const SIZE_THRESHOLDS = {
  green_max: 15 * 1024 * 1024,   // ≤ 15 MB → green, recommended
  amber_max: 30 * 1024 * 1024,   // 15-30 MB → amber, slower
  red_max: 200 * 1024 * 1024,    // 30-200 MB → red, likely to fail
  hard_cap: 200 * 1024 * 1024,   // > 200 MB → server rejects
};

const _sizeStatus = (bytes) => {
  if (!bytes) return null;
  if (bytes <= SIZE_THRESHOLDS.green_max) return "green";
  if (bytes <= SIZE_THRESHOLDS.amber_max) return "amber";
  if (bytes <= SIZE_THRESHOLDS.red_max) return "red";
  return "block";
};

const _fmtBytes = (bytes) => {
  if (!bytes) return "0 KB";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

function FileSizeBanner() {
  return (
    <div className="border border-border p-3 text-[11px] bg-muted/30" data-testid="file-size-guidance">
      <div className="font-mono uppercase text-[10px] text-accent tracking-widest mb-1.5">Recommended Tally file size</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" /><span><b>≤ 15 MB</b> · recommended (1 FY)</span></div>
        <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" /><span><b>15–30 MB</b> · works, slower</span></div>
        <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500 shrink-0" /><span><b>30–200 MB</b> · may fail — split by FY</span></div>
        <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-neutral-500 shrink-0" /><span><b>&gt; 200 MB</b> · blocked</span></div>
      </div>
      <div className="text-muted-foreground mt-2 leading-relaxed">
        For files above 15 MB, export from Tally <b>one financial year at a time</b> (Gateway → Display More Reports → List of Accounts → set date range to a single FY → Alt+E → XML).
      </div>
    </div>
  );
}

function SizePill({ bytes }) {
  const status = _sizeStatus(bytes);
  if (!status) return null;
  const cls = {
    green: "text-emerald-500 border-emerald-500 bg-emerald-500/5",
    amber: "text-amber-500 border-amber-500 bg-amber-500/5",
    red: "text-red-500 border-red-500 bg-red-500/5",
    block: "text-red-600 border-red-600 bg-red-600/10",
  }[status];
  const label = {
    green: "RECOMMENDED",
    amber: "SLOWER",
    red: "MAY FAIL · SPLIT BY FY",
    block: "BLOCKED · MAX 200 MB",
  }[status];
  return (
    <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 border ${cls}`} data-testid="file-size-pill">
      {_fmtBytes(bytes)} · {label}
    </span>
  );
}

// Confidence pill — reused by both Master Mapping and OB tables
function ConfidencePill({ level }) {
  if (!level) return null;
  const map = {
    high:   ["text-emerald-500 border-emerald-500", "AUTO ✓"],
    medium: ["text-blue-500 border-blue-500", "AUTO"],
    low:    ["text-amber-500 border-amber-500", "REVIEW"],
  };
  const [cls, label] = map[level] || map.low;
  return <span className={`text-[9px] font-mono uppercase px-1 py-0.5 border ${cls}`} data-testid={`confidence-pill-${level}`}>{label}</span>;
}

/** Side-by-side reconciliation table — original Tally values vs what will
 * land in Zenith after import. Lets the accountant eyeball every ledger
 * before committing. */
function LedgerDiffTable({ ledgers, overrides, existingMasters }) {
  const [filter, setFilter] = React.useState("");
  const [onlyMismatch, setOnlyMismatch] = React.useState(false);
  if (!ledgers?.length) return <div className="text-xs text-muted-foreground p-2">No ledgers to reconcile.</div>;

  const acctById = new Map((existingMasters?.accounts || []).map(a => [a.id, a]));
  const partyById = new Map((existingMasters?.parties || []).map(p => [p.id, p]));

  const rows = ledgers.map(l => {
    const lc = l.name.toLowerCase();
    const ov = overrides[lc] || {};
    const cat = ov.category || l.suggested_category || "gl";
    const mapId = ov.map_to_existing_id;
    let targetName = l.name;
    let targetKind = "new";
    let targetDetails = "";
    if (cat === "skip") {
      targetName = "—"; targetKind = "skip"; targetDetails = "will be skipped";
    } else if (mapId) {
      const a = acctById.get(mapId), p = partyById.get(mapId);
      const m = a || p;
      targetName = m ? m.name : "(missing)";
      targetKind = "mapped";
      targetDetails = a ? `${a.account_type}/${a.subtype || "-"}` : (p ? `party·${p.party_type}` : "");
    } else {
      const root = ov.account_type || l.suggested_account_type || "";
      const sub = ov.gl_subcategory || l.suggested_gl_subcategory || "";
      targetKind = "new";
      targetDetails = cat === "gl" ? `${root}${sub ? `/${sub}` : ""}`
                     : cat === "ar" ? "party·customer"
                     : cat === "ap" ? "party·vendor"
                     : cat === "fixed_asset" ? "asset·fixed_asset" : cat;
    }
    // Reconciliation state: MATCH if mapped to existing with same name; DIFF if renamed or new
    const nameChanged = mapId && targetName.toLowerCase() !== l.name.toLowerCase();
    const status = targetKind === "skip" ? "skip"
                    : targetKind === "mapped" ? (nameChanged ? "match_renamed" : "match")
                    : "new";
    return {
      lc, source_name: l.name, source_parent: l.parent || "—",
      source_opening: l.opening_balance || 0,
      target_name: targetName, target_details: targetDetails,
      target_kind: targetKind, status, category: cat,
    };
  });

  const q = filter.toLowerCase();
  const visible = rows.filter(r => {
    if (onlyMismatch && (r.status === "match")) return false;
    if (!q) return true;
    return r.source_name.toLowerCase().includes(q) || r.target_name.toLowerCase().includes(q);
  });

  const stats = rows.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

  const badge = (s) => {
    const map = {
      match: ["text-emerald-500 border-emerald-500", "MATCH"],
      match_renamed: ["text-amber-500 border-amber-500", "MAPPED · RENAMED"],
      new: ["text-blue-500 border-blue-500", "NEW"],
      skip: ["text-muted-foreground border-muted-foreground", "SKIP"],
    };
    const [cls, label] = map[s] || map.new;
    return <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 border ${cls}`}>{label}</span>;
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 flex-wrap">
        <Input placeholder="Filter reconciliation…" value={filter}
          onChange={e => setFilter(e.target.value)} className="max-w-[260px] text-xs h-8"
          data-testid="tally-diff-filter" />
        <label className="flex items-center gap-1.5 text-[11px] cursor-pointer">
          <input type="checkbox" checked={onlyMismatch} onChange={e => setOnlyMismatch(e.target.checked)}
            data-testid="tally-diff-mismatch-only" />
          Only show non-matches
        </label>
        <div className="ml-auto flex gap-3 text-[10px] font-mono">
          <span className="text-emerald-500">MATCH {stats.match || 0}</span>
          <span className="text-amber-500">RENAMED {stats.match_renamed || 0}</span>
          <span className="text-blue-500">NEW {stats.new || 0}</span>
          <span className="text-muted-foreground">SKIP {stats.skip || 0}</span>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[440px] overflow-y-auto border border-border">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-card z-10">
            <tr className="border-b">
              <th className="text-left py-2 px-2 uppercase text-[10px]">Tally ledger (source)</th>
              <th className="text-left uppercase text-[10px]">Tally group</th>
              <th className="text-right uppercase text-[10px]">Opening (₹)</th>
              <th className="text-center py-2 px-2 uppercase text-[10px]">→</th>
              <th className="text-left uppercase text-[10px]">Zenith target</th>
              <th className="text-left uppercase text-[10px]">Category</th>
              <th className="text-left uppercase text-[10px]">Status</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r, i) => (
              <tr key={r.lc} className={`border-b hover:bg-muted/40 ${r.status === "skip" ? "opacity-50" : ""}`}
                data-testid={`tally-diff-row-${i}`}>
                <td className="py-1.5 px-2 font-medium">{r.source_name}</td>
                <td className="text-muted-foreground">{r.source_parent}</td>
                <td className="text-right font-mono">{fmtINR(r.source_opening)}</td>
                <td className="text-center text-muted-foreground">→</td>
                <td className="font-medium">{r.target_name}</td>
                <td className="text-[10px] font-mono uppercase text-muted-foreground">{r.target_details || r.category}</td>
                <td>{badge(r.status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-muted-foreground italic">
        Showing {visible.length} of {rows.length} ledger(s). Change category / mapping in the
        table above and this reconciliation updates instantly.
      </div>
    </div>
  );
}

function HelpDrawer() {
  return (
    <div className="card-sharp p-5 border-l-4 border-l-accent" data-testid="tally-help">
      <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">How to Export from Tally Prime EL</div>
      <ol className="text-sm space-y-2 list-decimal pl-5">
        <li>Open your company in Tally Prime EL (<b>Company Info → Restore</b> if you only have a <code>.tbk</code>).</li>
        <li>Go to <b>Gateway of Tally → Display More Reports → List of Accounts</b> (masters only), or <b>Day Book</b> (masters + vouchers).</li>
        <li>Press <b>Alt + E → Export</b>. Set <b>Format = XML</b>, choose the date range, and export.</li>
        <li>Upload the resulting <b>.xml</b> file on the <b>Upload</b> tab.</li>
      </ol>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="card-sharp p-4" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-2xl font-mono mt-1">{value}</div>
    </div>
  );
}

function CompanyVerificationCard({ verification, ack, setAck, parsedToken, onSynced }) {
  const [syncing, setSyncing] = React.useState(false);
  const { source, target, identity_match } = verification || {};
  if (!source) return null;
  const match = identity_match === "match";
  const mismatch = identity_match === "mismatch";
  const canPrefill = source.name && (!target?.name || !target?.gstin);
  const borderClass = match ? "border-l-green-500" : mismatch ? "border-l-red-500" : "border-l-orange-500";
  const doSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/tally/sync-company-to-org", { parsed_token: parsedToken });
      toast.success(`Company profile updated · ${r.data?.updated || 0} field(s)`);
      onSynced && onSynced();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    setSyncing(false);
  };
  return (
    <div className={`card-sharp p-5 border-l-4 ${borderClass}`} data-testid="company-verification">
      <div className="flex items-center gap-2 mb-3">
        <Building2 className="w-4 h-4 text-accent" />
        <span className="text-[10px] font-mono uppercase tracking-widest text-accent">Company Identity Verification</span>
        {match && <span className="ml-auto text-xs text-green-500 flex items-center gap-1" data-testid="match-badge"><CheckCircle2 className="w-3.5 h-3.5" /> MATCH</span>}
        {mismatch && <span className="ml-auto text-xs text-red-500 flex items-center gap-1" data-testid="mismatch-badge"><ShieldAlert className="w-3.5 h-3.5" /> MISMATCH — REVIEW</span>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div className="border border-border p-3" data-testid="source-company">
          <div className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Source · From Tally File</div>
          <div className="text-base font-semibold">{source.name || "(not detected)"}</div>
          <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
            {source.gstin && <div>GSTIN: <span className="font-mono">{source.gstin}</span></div>}
            {source.pan && <div>PAN: <span className="font-mono">{source.pan}</span></div>}
            {source.fy_from && <div>FY: {source.fy_from} → {source.fy_to}</div>}
          </div>
        </div>
        <div className="border border-border p-3" data-testid="target-company">
          <div className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Target · Your Zenith Org</div>
          <div className="text-base font-semibold">{target?.name || "(current org)"}</div>
          <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
            {target?.gstin ? <div>GSTIN: <span className="font-mono">{target.gstin}</span></div> : <div className="text-orange-500">GSTIN not set — update org profile</div>}
          </div>
          {canPrefill && (
            <Button size="sm" variant="outline" onClick={doSync} disabled={syncing} className="mt-3 rounded-sm text-xs" data-testid="sync-company-btn">
              {syncing ? "Syncing…" : "Pre-fill my org with these details"}
            </Button>
          )}
        </div>
      </div>
      {mismatch && (
        <label className="mt-4 flex items-start gap-3 p-3 border border-red-500/40 bg-red-500/5 cursor-pointer" data-testid="mismatch-ack">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-1" data-testid="mismatch-ack-checkbox" />
          <div className="text-xs">I confirm this is intentional — import <b>{source.name}</b> ({source.gstin}) into target <b>{target?.name}</b> ({target?.gstin}) anyway.</div>
        </label>
      )}
    </div>
  );
}

const ROOT_TYPES = ["asset", "liability", "equity", "income", "expense"];

function LedgerMappingTable({ ledgers, overrides, setOverrides, existingMasters, categoryTree }) {
  const [filter, setFilter] = React.useState("");
  const [catFilter, setCatFilter] = React.useState("all");
  const [ambiguousOnly, setAmbiguousOnly] = React.useState(true);
  const [selected, setSelected] = React.useState(() => new Set());
  const [bulkRoot, setBulkRoot] = React.useState("");
  const [bulkSub, setBulkSub] = React.useState("");
  if (!ledgers?.length) return <div className="text-xs text-muted-foreground p-2">No ledgers detected in the Tally export.</div>;
  const confidenceStats = ledgers.reduce((acc, l) => {
    const c = l.confidence || (l.is_ambiguous ? "low" : "high");
    acc[c] = (acc[c] || 0) + 1;
    return acc;
  }, {});
  const ambiguousCount = ledgers.filter(l => (l.confidence === "low") || l.is_ambiguous).length;
  const setRow = (name, patch) => {
    const key = name.toLowerCase();
    setOverrides({ ...overrides, [key]: { ...(overrides[key] || {}), ...patch } });
  };
  const bulkAutoMap = () => {
    const acctByName = new Map((existingMasters?.accounts || []).map(a => [a.name.toLowerCase(), a]));
    const pByName = new Map((existingMasters?.parties || []).map(p => [p.name.toLowerCase(), p]));
    const next = { ...overrides };
    let matched = 0;
    for (const l of ledgers) {
      const lc = l.name.toLowerCase();
      const a = acctByName.get(lc);
      const p = pByName.get(lc);
      if (a) { next[lc] = { ...(next[lc] || {}), category: "gl", map_to_existing_id: a.id }; matched++; }
      else if (p) { next[lc] = { ...(next[lc] || {}), category: p.party_type === "vendor" ? "ap" : "ar", map_to_existing_id: p.id }; matched++; }
    }
    setOverrides(next);
    toast.success(`Auto-mapped ${matched} ledger(s) to existing Zenith masters`);
  };
  const visible = ledgers.filter(l => {
    if (filter && !(l.name.toLowerCase().includes(filter.toLowerCase()) || (l.parent || "").toLowerCase().includes(filter.toLowerCase()))) return false;
    if (catFilter !== "all") {
      const cur = (overrides[l.name.toLowerCase()] || {}).category || l.suggested_category;
      if (cur !== catFilter) return false;
    }
    if (ambiguousOnly && !(l.confidence === "low" || l.is_ambiguous)) return false;
    return true;
  });

  // Helpers for the current effective category of a row — parties are locked out of GL editor
  const effCat = (l) => (overrides[l.name.toLowerCase()] || {}).category || l.suggested_category || "gl";
  const isGLish = (l) => {
    const c = effCat(l);
    return c === "gl" || c === "fixed_asset";
  };
  const effRoot = (l) => {
    const ov = overrides[l.name.toLowerCase()] || {};
    if (ov.account_type) return ov.account_type;
    if (effCat(l) === "fixed_asset") return "asset";
    return l.suggested_account_type || "asset";
  };
  const effSub = (l) => {
    const ov = overrides[l.name.toLowerCase()] || {};
    if (ov.gl_subcategory) return ov.gl_subcategory;
    if (effCat(l) === "fixed_asset") return "fixed_asset";
    return l.suggested_gl_subcategory || "";
  };
  const subLabel = (root, code) => {
    const arr = categoryTree?.[root] || [];
    const hit = arr.find(c => c.code === code);
    return hit ? hit.label : code;
  };

  // Selection helpers — only GL-ish rows can be multi-selected for bulk categorization
  const selectableVisible = visible.filter(isGLish);
  const allChecked = selectableVisible.length > 0 && selectableVisible.every(l => selected.has(l.name.toLowerCase()));
  const someChecked = !allChecked && selectableVisible.some(l => selected.has(l.name.toLowerCase()));
  const toggleAllVisible = () => {
    const s = new Set(selected);
    if (allChecked) selectableVisible.forEach(l => s.delete(l.name.toLowerCase()));
    else selectableVisible.forEach(l => s.add(l.name.toLowerCase()));
    setSelected(s);
  };
  const toggleOne = (lc) => {
    const s = new Set(selected);
    s.has(lc) ? s.delete(lc) : s.add(lc);
    setSelected(s);
  };
  const clearSelection = () => setSelected(new Set());

  const applyBulk = () => {
    if (selected.size === 0) return toast.error("Select at least one ledger");
    if (!bulkRoot && !bulkSub) return toast.error("Choose a root and/or a Sch III sub-category to apply");
    // If sub is set, ensure it belongs to the picked root (if root also set)
    if (bulkRoot && bulkSub) {
      const allowed = (categoryTree?.[bulkRoot] || []).map(c => c.code);
      if (!allowed.includes(bulkSub)) return toast.error(`'${bulkSub}' is not valid for '${bulkRoot}'`);
    }
    const next = { ...overrides };
    let applied = 0;
    for (const l of ledgers) {
      const lc = l.name.toLowerCase();
      if (!selected.has(lc)) continue;
      const patch = { ...(next[lc] || {}) };
      if (bulkRoot) patch.account_type = bulkRoot;
      if (bulkSub) patch.gl_subcategory = bulkSub;
      // Ensure category is not "skip" — bulk implies user wants these categorized as GL
      if (!patch.category || patch.category === "skip") patch.category = "gl";
      next[lc] = patch;
      applied++;
    }
    setOverrides(next);
    toast.success(`Applied categorization to ${applied} ledger(s)`);
  };

  const bulkSubs = bulkRoot ? (categoryTree?.[bulkRoot] || []) : [];

  return (
    <div>
      <div className="border border-border p-2.5 mb-2 bg-muted/20 text-[11px] leading-snug" data-testid="ledger-cat-summary">
        <div className="flex items-start gap-2">
          <Wand2 className="w-3.5 h-3.5 mt-0.5 text-accent shrink-0" />
          <div className="flex-1">
            <b>Tally Groups → Zenith category auto-mapping.</b> Ledgers whose Tally parent group is a
            recognised primary group (Sundry Debtors, Fixed Assets, Purchase Accounts, …) are pre-categorised.
            Only <b className="text-amber-500">{ambiguousCount} ambiguous</b> row(s) need your attention.
            <span className="ml-2 text-emerald-500">✓ {confidenceStats.high || 0} high</span>
            <span className="ml-2 text-blue-500">· {confidenceStats.medium || 0} via parent chain</span>
            <span className="ml-2 text-amber-500">· {confidenceStats.low || 0} need review</span>
          </div>
        </div>
      </div>
      <div className="flex gap-2 mb-2 flex-wrap items-center">
        <Input placeholder="Filter by name or Tally group…" value={filter} onChange={e => setFilter(e.target.value)} className="max-w-[260px] text-xs" data-testid="ledger-filter" />
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-[180px] text-xs" data-testid="ledger-cat-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none px-2 py-1 border border-amber-500/40 bg-amber-500/5">
          <input type="checkbox" checked={ambiguousOnly} onChange={e => setAmbiguousOnly(e.target.checked)}
            data-testid="ledger-ambiguous-only" />
          <span>Show only ambiguous ({ambiguousCount})</span>
        </label>
        <Button size="sm" variant="outline" onClick={bulkAutoMap} data-testid="bulk-automap-btn"><Wand2 className="w-3 h-3 mr-1" />Bulk auto-map to existing</Button>
        <span className="text-[10px] text-muted-foreground ml-auto">Showing {visible.length} of {ledgers.length}</span>
      </div>

      {/* Bulk categorization toolbar — visible whenever at least one GL row is selected */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-2 p-2 border border-accent/40 bg-accent/5" data-testid="bulk-cat-toolbar">
          <span className="text-[11px] font-mono uppercase text-accent">{selected.size} selected</span>
          <span className="text-[10px] text-muted-foreground">— set for all:</span>
          <Select value={bulkRoot} onValueChange={(v) => { setBulkRoot(v); setBulkSub(""); }}>
            <SelectTrigger className="w-[150px] h-7 text-xs rounded-sm" data-testid="bulk-root-select">
              <SelectValue placeholder="Root category" />
            </SelectTrigger>
            <SelectContent>
              {ROOT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={bulkSub} onValueChange={setBulkSub} disabled={!bulkRoot}>
            <SelectTrigger className="w-[240px] h-7 text-xs rounded-sm" data-testid="bulk-sub-select">
              <SelectValue placeholder={bulkRoot ? "Sch III sub-category" : "Pick root first"} />
            </SelectTrigger>
            <SelectContent className="max-h-[280px]">
              {bulkSubs.map(s => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={applyBulk} data-testid="bulk-apply-btn">Apply</Button>
          <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="bulk-clear-btn">Clear</Button>
        </div>
      )}

      <div className="overflow-x-auto max-h-[420px] overflow-y-auto border border-border">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-card z-10">
            <tr className="border-b">
              <th className="text-left py-2 px-2 uppercase text-[10px] w-[28px]">
                <input
                  type="checkbox"
                  checked={allChecked}
                  ref={el => { if (el) el.indeterminate = someChecked; }}
                  onChange={toggleAllVisible}
                  data-testid="ledger-check-all"
                  aria-label="Select all visible GL ledgers"
                />
              </th>
              <th className="text-left uppercase text-[10px]">Tally Ledger</th>
              <th className="text-left uppercase text-[10px]">Parent Group</th>
              <th className="text-left uppercase text-[10px]">Confidence</th>
              <th className="text-left uppercase text-[10px]">Category</th>
              <th className="text-left uppercase text-[10px]">Root</th>
              <th className="text-left uppercase text-[10px]">Sch III Sub-category</th>
              <th className="text-left uppercase text-[10px]">Map to existing</th>
              <th className="text-right uppercase text-[10px]">Opening</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((l, i) => {
              const lc = l.name.toLowerCase();
              const ov = overrides[lc] || {};
              const cat = ov.category || l.suggested_category || "gl";
              const mapId = ov.map_to_existing_id || "";
              const gl = cat === "gl" || cat === "fixed_asset";
              const options = (cat === "ar" || cat === "ap")
                ? (existingMasters?.parties || []).filter(p => p.party_type === (cat === "ar" ? "customer" : "vendor"))
                : (existingMasters?.accounts || []);
              const isMatch = !!l.existing_match_id;
              const root = effRoot(l);
              const sub = effSub(l);
              const subs = categoryTree?.[root] || [];
              return (
                <tr key={i} className={`border-b ${cat === "skip" ? "opacity-40" : ""}`} data-testid={`ledger-row-${i}`}>
                  <td className="px-2">
                    {gl && (
                      <input
                        type="checkbox"
                        checked={selected.has(lc)}
                        onChange={() => toggleOne(lc)}
                        data-testid={`ledger-check-${i}`}
                        aria-label={`Select ${l.name}`}
                      />
                    )}
                  </td>
                  <td className="py-1.5 px-2 font-medium">
                    {l.name}
                    {isMatch && <span className="ml-2 text-[9px] font-mono uppercase text-green-500">✓ EXISTS</span>}
                  </td>
                  <td className="text-muted-foreground" title={(l.parent_chain || []).join(" → ")}>
                    {l.parent || "—"}
                    {l.parent_chain && l.parent_chain.length > 1 && (
                      <span className="ml-1 text-[9px] text-muted-foreground/60">↑{l.parent_chain.length - 1}</span>
                    )}
                  </td>
                  <td>
                    <ConfidencePill level={l.confidence || (l.is_ambiguous ? "low" : "high")} />
                  </td>
                  <td>
                    <Select value={cat} onValueChange={(v) => setRow(l.name, { category: v, ...(v === "skip" ? { map_to_existing_id: "" } : {}) })}>
                      <SelectTrigger className="text-xs h-7 rounded-sm" data-testid={`cat-select-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td>
                    {gl ? (
                      <Select value={root} onValueChange={(v) => setRow(l.name, { account_type: v, gl_subcategory: "" })}>
                        <SelectTrigger className="text-xs h-7 rounded-sm min-w-[110px]" data-testid={`root-select-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ROOT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-[10px] text-muted-foreground italic">party</span>
                    )}
                  </td>
                  <td>
                    {gl ? (
                      <Select value={sub || ""} onValueChange={(v) => setRow(l.name, { gl_subcategory: v })}>
                        <SelectTrigger className="text-xs h-7 rounded-sm min-w-[200px]" data-testid={`sub-select-${i}`}>
                          <SelectValue placeholder="Auto-derive" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[280px]">
                          {subs.map(s => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-[10px] text-muted-foreground italic">—</span>
                    )}
                  </td>
                  <td>
                    <Select value={mapId || "__new"} onValueChange={(v) => setRow(l.name, { map_to_existing_id: v === "__new" ? "" : v })}>
                      <SelectTrigger className="text-xs h-7 rounded-sm min-w-[180px]" data-testid={`map-select-${i}`}><SelectValue placeholder="Create new" /></SelectTrigger>
                      <SelectContent className="max-h-[280px]">
                        <SelectItem value="__new">— Create new master —</SelectItem>
                        {options.map(o => <SelectItem key={o.id} value={o.id}>{o.code ? o.code + " · " : ""}{o.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="text-right font-mono">{fmtINR(l.opening_balance || 0)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-[10px] text-muted-foreground italic">
        Root + Sch III Sub-category are editable only for GL / Fixed Asset ledgers. Party ledgers (AR/AP) inherit their control-account category.
      </div>
    </div>
  );
}

function PreviewPanel({ preview, options, setOptions, ack, setAck, ledgerOverrides, setLedgerOverrides,
                          existingMasters, categoryTree, onCommitStage, committing, onSynced,
                          onDownloadPreviewPDF, vtypeMapPersistedAt }) {
  const mismatch = preview.company_verification?.identity_match === "mismatch";
  const disable = committing || (mismatch && !ack);
  return (
    <div className="space-y-4" data-testid="tally-preview">
      {onDownloadPreviewPDF && (
        <div className="flex items-center justify-end -mb-2">
          <Button variant="outline" size="sm" onClick={onDownloadPreviewPDF}
                  className="rounded-sm gap-1.5" data-testid="download-preview-pdf-btn">
            <Download className="w-3.5 h-3.5" /> Download preview PDF · share with CA
          </Button>
        </div>
      )}
      <CompanyVerificationCard verification={preview.company_verification} ack={ack} setAck={setAck} parsedToken={preview.parsed_token} onSynced={onSynced} />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard label="Groups" value={preview.summary.groups_found} testid="stat-groups" />
        <StatCard label="Ledgers" value={preview.summary.ledgers_found} testid="stat-ledgers" />
        <StatCard label="Stock Items" value={preview.summary.stock_items_found} testid="stat-items" />
        <StatCard label="Dimensions" value={preview.summary.cost_centres_found} testid="stat-cc" />
        <StatCard label="Vouchers" value={preview.summary.vouchers_found} testid="stat-vch" />
      </div>

      {preview.warnings?.length > 0 && (
        <div className="card-sharp p-4 border-l-4 border-l-orange-500" data-testid="warnings">
          <div className="flex items-center gap-2 text-orange-500 mb-2"><AlertTriangle className="w-4 h-4" /><span className="font-semibold text-sm">Warnings</span></div>
          <ul className="text-sm list-disc pl-5">{preview.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      )}

      {/* Iter-198 · Voucher-type mapping picker moved to TOP so accountants confirm it FIRST. */}
      {preview.voucher_type_summary?.length > 0 && (
        <VoucherTypeMap
          summary={preview.voucher_type_summary}
          value={options.voucher_type_map || {}}
          onChange={(m) => setOptions({ ...options, voucher_type_map: m })}
          persistedAt={vtypeMapPersistedAt}
          testidPrefix="tally-vtype" />
      )}

      <div className="card-sharp p-4" data-testid="ledger-mapping">
        <div className="text-xs font-mono uppercase tracking-widest text-accent mb-1">Master Mapping · {preview.ledgers_mapping?.length || 0} ledgers</div>
        <div className="text-[11px] text-muted-foreground mb-3">
          Choose a <b>Category</b> for each Tally ledger: GL Account, AR (Customer), AP (Vendor), or Fixed Asset (sub-ledger).
          Use <b>Map to existing</b> to link a Tally ledger to a master that already exists in Zenith (prevents duplicates).
          Use <b>Bulk auto-map to existing</b> to match all identical names in one click.
        </div>
        <LedgerMappingTable ledgers={preview.ledgers_mapping || []} overrides={ledgerOverrides} setOverrides={setLedgerOverrides} existingMasters={existingMasters} categoryTree={categoryTree} />
      </div>

      <div className="card-sharp p-4" data-testid="ledger-diff-panel">
        <div className="flex items-center gap-2 mb-1">
          <ArrowLeftRight className="w-4 h-4 text-accent" />
          <div className="text-xs font-mono uppercase tracking-widest text-accent">
            Reconciliation Diff · Tally values → Zenith after import
          </div>
        </div>
        <div className="text-[11px] text-muted-foreground mb-3">
          Side-by-side view of every ledger. Toggle <b>Only show non-matches</b> to spot the
          ones Zenith will create or rename before you commit.
        </div>
        <LedgerDiffTable ledgers={preview.ledgers_mapping || []} overrides={ledgerOverrides} existingMasters={existingMasters} />
      </div>

      <div className="card-sharp p-4" data-testid="voucher-preview-stats">
        <div className="text-xs font-mono uppercase tracking-widest text-accent mb-2">Vouchers · {preview.vouchers.to_create} to import</div>
        <div className="text-xs text-muted-foreground">Vouchers preserve Tally type (Sales / Purchase / Receipt / Payment / Contra / Journal) and post chronologically.</div>
        {preview.vouchers.unresolved_ledgers?.length > 0 && (
          <div className="mt-2 text-[11px] text-orange-500">Unresolved ledger references (will auto-create if enabled): {preview.vouchers.unresolved_ledgers.join(", ")}</div>
        )}
      </div>

      <div className="card-sharp p-5" data-testid="commit-options">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-4">Staged Import — Run in order</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <label className="flex items-center gap-3 p-2 border border-border cursor-pointer" data-testid="opt-import_opening_balances">
            <Switch checked={!!options.import_opening_balances} onCheckedChange={(v) => setOptions({ ...options, import_opening_balances: v })} />
            <span className="text-sm">Post opening balances (auto-balanced OBE JV)</span>
          </label>
          <label className="flex items-center gap-3 p-2 border border-border cursor-pointer" data-testid="opt-auto_create_unmatched">
            <Switch checked={!!options.auto_create_unmatched} onCheckedChange={(v) => setOptions({ ...options, auto_create_unmatched: v })} />
            <span className="text-sm">Stage 2: Auto-create unmatched masters referenced by vouchers</span>
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="border border-border p-4" data-testid="stage-masters">
            <div className="text-xs font-mono uppercase tracking-widest text-accent mb-2">① Import Masters</div>
            <div className="text-[11px] text-muted-foreground mb-3">Chart of Accounts, Parties (AR/AP), Fixed Assets, Stock Items, opening balances.</div>
            <Button className="w-full" onClick={() => onCommitStage("masters")} disabled={disable} data-testid="commit-masters-btn">
              {committing === "masters" ? "Importing…" : "Import Masters →"}
            </Button>
          </div>
          <div className="border border-border p-4" data-testid="stage-transactions">
            <div className="text-xs font-mono uppercase tracking-widest text-accent mb-2">② Import Transactions</div>
            <div className="text-[11px] text-muted-foreground mb-3">Historical vouchers (Sales / Purchase / Receipt / Payment / Contra / Journal). Uses masters from step ①.</div>
            <Button className="w-full" onClick={() => onCommitStage("transactions")} disabled={disable} data-testid="commit-transactions-btn">
              {committing === "transactions" ? "Importing…" : "Import Transactions →"}
            </Button>
          </div>
          <div className="border border-dashed border-border p-4" data-testid="stage-both">
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Or — do both in one shot</div>
            <div className="text-[11px] text-muted-foreground mb-3">For small datasets you can commit masters + transactions together (single batch).</div>
            <Button variant="outline" className="w-full" onClick={() => onCommitStage("both")} disabled={disable} data-testid="commit-both-btn">
              {committing === "both" ? "Importing…" : "Import All"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ResultPanel({ result, batchId, onReset, onErase }) {
  const dl = () => downloadBlob(`/tally/batches/${batchId}/report.xlsx`, `tally_migration_report_${batchId.slice(0,8)}.xlsx`);
  return (
    <div className="space-y-4" data-testid="commit-result">
      <div className="card-sharp p-5 border-l-4 border-l-green-500">
        <div className="flex items-center gap-2 text-green-500 mb-3">
          <CheckCircle2 className="w-5 h-5" /><span className="font-semibold">Migration completed</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          <StatCard label="Accounts created" value={result.accounts_created} testid="r-accounts" />
          <StatCard label="Parties created" value={result.parties_created} testid="r-parties" />
          <StatCard label="Items created" value={result.items_created} testid="r-items" />
          <StatCard label="Vouchers created" value={result.vouchers_created} testid="r-vouchers" />
        </div>
        {result.tx_summary && (
          <div className="grid grid-cols-4 gap-3">
            <StatCard label="Tx total" value={result.tx_summary.total} testid="r-tx-total" />
            <StatCard label="✓ Success" value={result.tx_summary.success} testid="r-tx-success" />
            <StatCard label="⚠ Skipped" value={result.tx_summary.skipped} testid="r-tx-skipped" />
            <StatCard label="✗ Failed" value={result.tx_summary.failed} testid="r-tx-failed" />
          </div>
        )}
        {result.errors?.length > 0 && (
          <div className="mt-4 text-sm">
            <div className="text-orange-500 font-semibold mb-1">{result.errors.length} errors:</div>
            <ul className="list-disc pl-5 text-xs">{result.errors.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}</ul>
          </div>
        )}
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button onClick={dl} data-testid="download-report-btn"><Download className="w-4 h-4 mr-2" />Download Migration Report (Excel)</Button>
        <Button variant="outline" onClick={onReset} data-testid="reset-btn">Migrate another file</Button>
        {/* Iter-198 · Erase THIS batch right here — user shouldn't have to leave the screen to undo. */}
        <Button variant="outline" onClick={onErase} data-testid="erase-this-batch-btn"
                className="text-red-600 border-red-500/60 hover:bg-red-50 hover:border-red-500">
          Erase This Import
        </Button>
      </div>
    </div>
  );
}

function ReviewCategorizationPane({ categoryTree }) {
  const [accounts, setAccounts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [scope, setScope] = React.useState("tally_import"); // tally_import | all
  const [filter, setFilter] = React.useState("");
  const [selected, setSelected] = React.useState(() => new Set());
  const [bulkRoot, setBulkRoot] = React.useState("");
  const [bulkSub, setBulkSub] = React.useState("");
  const [applying, setApplying] = React.useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = scope === "tally_import" ? { source: "tally_import" } : {};
      const r = await api.get("/accounts/uncategorized", { params });
      setAccounts(r.data.accounts || []);
      setSelected(new Set());
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    setLoading(false);
  };
  React.useEffect(() => { load(); }, [scope]);

  const visible = accounts.filter(a => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (a.name || "").toLowerCase().includes(q) || (a.code || "").toLowerCase().includes(q);
  });
  const allChecked = visible.length > 0 && visible.every(a => selected.has(a.id));
  const someChecked = !allChecked && visible.some(a => selected.has(a.id));
  const toggleAll = () => {
    const s = new Set(selected);
    if (allChecked) visible.forEach(a => s.delete(a.id));
    else visible.forEach(a => s.add(a.id));
    setSelected(s);
  };
  const toggleOne = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const bulkSubs = bulkRoot ? (categoryTree?.[bulkRoot] || []) : [];

  const apply = async () => {
    if (selected.size === 0) return toast.error("Select at least one account");
    if (!bulkSub) return toast.error("Choose a Sch III sub-category");
    setApplying(true);
    try {
      const r = await api.post("/accounts/bulk-categorize", {
        account_ids: Array.from(selected),
        gl_subcategory: bulkSub,
        ...(bulkRoot ? { account_type: bulkRoot } : {}),
      });
      const { updated, skipped, errors } = r.data;
      if (updated > 0) toast.success(`Updated ${updated} account(s)${skipped ? ` · skipped ${skipped}` : ""}`);
      if (skipped > 0) toast.error(`${skipped} skipped — check validation errors`);
      if (errors?.length) console.warn("Bulk-categorize errors:", errors);
      await load();
      setBulkRoot(""); setBulkSub("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk update failed"); }
    setApplying(false);
  };

  const subLabel = (root, code) => {
    const arr = categoryTree?.[root] || [];
    const hit = arr.find(c => c.code === code);
    return hit ? hit.label : code;
  };

  return (
    <div className="card-sharp p-5 space-y-4" data-testid="review-cat-panel">
      <div className="text-sm text-muted-foreground">
        Accounts still sitting in <b>generic Schedule III buckets</b> (Other Current Asset / Liability, Other Income / Expense, etc.) — usually Tally imports that couldn&apos;t be auto-classified.
        Select rows and apply a specific Schedule III sub-category to move them to the right bucket.
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-[220px] text-xs h-8 rounded-sm" data-testid="review-scope">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tally_import">Tally-imported accounts only</SelectItem>
            <SelectItem value="all">All accounts (any source)</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="Filter by code / name…" value={filter} onChange={e => setFilter(e.target.value)} className="max-w-[260px] text-xs h-8" data-testid="review-filter" />
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="review-reload">
          {loading ? "Loading…" : "Reload"}
        </Button>
        <span className="text-[10px] text-muted-foreground ml-auto">
          {accounts.length} account(s) need review · showing {visible.length}
        </span>
      </div>

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 p-2 border border-accent/40 bg-accent/5" data-testid="review-bulk-toolbar">
          <span className="text-[11px] font-mono uppercase text-accent">{selected.size} selected</span>
          <span className="text-[10px] text-muted-foreground">— set:</span>
          <Select value={bulkRoot} onValueChange={(v) => { setBulkRoot(v); setBulkSub(""); }}>
            <SelectTrigger className="w-[150px] h-7 text-xs rounded-sm" data-testid="review-bulk-root">
              <SelectValue placeholder="Root (optional)" />
            </SelectTrigger>
            <SelectContent>
              {ROOT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={bulkSub} onValueChange={setBulkSub} disabled={!bulkRoot}>
            <SelectTrigger className="w-[260px] h-7 text-xs rounded-sm" data-testid="review-bulk-sub">
              <SelectValue placeholder={bulkRoot ? "Sch III sub-category" : "Pick root first"} />
            </SelectTrigger>
            <SelectContent className="max-h-[280px]">
              {bulkSubs.map(s => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={apply} disabled={applying} data-testid="review-bulk-apply">
            {applying ? "Applying…" : "Apply to selected"}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="review-bulk-clear">Clear</Button>
        </div>
      )}

      <div className="overflow-x-auto max-h-[500px] overflow-y-auto border border-border">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-card z-10">
            <tr className="border-b">
              <th className="text-left py-2 px-2 uppercase text-[10px] w-[28px]">
                <input
                  type="checkbox"
                  checked={allChecked}
                  ref={el => { if (el) el.indeterminate = someChecked; }}
                  onChange={toggleAll}
                  data-testid="review-check-all"
                  aria-label="Select all"
                />
              </th>
              <th className="text-left uppercase text-[10px]">Code</th>
              <th className="text-left uppercase text-[10px]">Account name</th>
              <th className="text-left uppercase text-[10px]">Root</th>
              <th className="text-left uppercase text-[10px]">Current Sch III bucket</th>
              <th className="text-left uppercase text-[10px]">Source</th>
              <th className="text-right uppercase text-[10px]">Balance</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan="7" className="text-center text-muted-foreground py-8">
                {loading ? "Loading…" : "No accounts need review — everything is properly classified."}
              </td></tr>
            )}
            {visible.map((a) => (
              <tr key={a.id} className="border-b hover:bg-muted/40" data-testid={`review-row-${a.code}`}>
                <td className="px-2">
                  <input
                    type="checkbox"
                    checked={selected.has(a.id)}
                    onChange={() => toggleOne(a.id)}
                    data-testid={`review-check-${a.code}`}
                    aria-label={`Select ${a.name}`}
                  />
                </td>
                <td className="font-mono">{a.code}</td>
                <td>{a.name}</td>
                <td className="text-[10px] font-mono uppercase text-muted-foreground">{a.account_type}</td>
                <td className="text-orange-500">{a.gl_subcategory ? subLabel(a.account_type, a.gl_subcategory) : <span className="italic">unset</span>}</td>
                <td className="text-[10px] font-mono text-muted-foreground">{a.source || "manual"}</td>
                <td className="text-right font-mono">{fmtINR(a.balance || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OpeningBalanceUploadPane({ categoryTree }) {
  const [file, setFile] = React.useState(null);
  const [asOf, setAsOf] = React.useState(new Date().toISOString().slice(0, 10));
  const [preview, setPreview] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [committing, setCommitting] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [existingAccounts, setExistingAccounts] = React.useState([]);
  // overrides shape: {ledger_lc: {action: 'existing'|'create'|'skip', account_id?, account_type?, gl_subcategory?, code?, name?}}
  const [overrides, setOverrides] = React.useState({});
  const [selected, setSelected] = React.useState(() => new Set());
  const [bulkRoot, setBulkRoot] = React.useState("");
  const [bulkSub, setBulkSub] = React.useState("");
  // Live per-row progress cursor while a commit is running — the backend
  // writes {pct, done, total, current_account, stage} to /tally/ob/progress/{commit_id}
  // during the request; we poll it in parallel so the "Committing…" wait is not opaque.
  const [obProgress, setObProgress] = React.useState(null);

  const loadAccounts = async () => {
    try { setExistingAccounts((await api.get("/accounts")).data || []); } catch(e) {}
  };
  React.useEffect(() => { loadAccounts(); }, []);

  const uploadFile = async () => {
    if (!file) return toast.error("Choose an Excel/CSV file first");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/tally/ob/preview`, fd);
      const j = r.data;
      setPreview(j);
      // Backend now returns `suggested_account_type`/`suggested_gl_subcategory`/
      // `confidence` on every unmatched row. Auto-seed overrides so that
      // confident matches are pre-filled — user only needs to confirm/edit
      // ambiguous rows.
      const seed = {};
      for (const u of (j.unmatched || [])) {
        seed[u.ledger.toLowerCase()] = {
          action: "create",
          account_type: u.suggested_account_type || _guessRoot(u.ledger),
          gl_subcategory: u.suggested_gl_subcategory || "",
        };
      }
      setOverrides(seed);
      setSelected(new Set());
      const highN = (j.confidence_stats?.high || 0);
      const medN = (j.confidence_stats?.medium || 0);
      const lowN = (j.confidence_stats?.low || 0);
      toast.success(`Parsed ${j.row_count} rows · matched ${j.matched.length}, unmatched ${j.unmatched.length}${lowN ? ` · ${lowN} need review` : ""}`);
    } catch (e) { toast.error(String(e.message || e)); }
    setBusy(false);
  };

  const commit = async () => {
    // Build override payload for the backend
    const payload = {};
    for (const [lc, ov] of Object.entries(overrides)) {
      if (ov.action === "skip") payload[lc] = { skip: true };
      else if (ov.action === "existing" && ov.account_id) payload[lc] = { account_id: ov.account_id };
      else if (ov.action === "create" && ov.account_type) {
        payload[lc] = {
          create_new: true,
          account_type: ov.account_type,
          gl_subcategory: ov.gl_subcategory || undefined,
          code: ov.code || undefined,
          name: ov.name || undefined,
        };
      }
    }
    // Validate: every unmatched row must have a decision (except skip is explicit)
    const missing = (preview.unmatched || []).filter(u => {
      const ov = overrides[u.ledger.toLowerCase()];
      if (!ov || ov.action === "create" && !ov.account_type) return true;
      if (ov.action === "existing" && !ov.account_id) return true;
      return false;
    });
    if (missing.length) {
      return toast.error(`Decide category for ${missing.length} unmatched ledger(s) — pick Skip, Map or Create new`);
    }

    setCommitting(true);
    // Generate a commit_id and start polling — the backend writes per-row
    // progress into `tb_progress` during the POST, so we can render a live
    // percentage + current-ledger banner while the request is in flight.
    const commit_id = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `ob-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    setObProgress({ pct: 0, done: 0, total: preview.row_count || 1, stage: "Starting…", current_account: "" });
    let stopPolling = false;
    const poll = async () => {
      while (!stopPolling) {
        try {
          const p = await api.get(`/tally/ob/progress/${commit_id}`);
          setObProgress(p.data);
        } catch (e) { /* silent */ }
        await new Promise(res => setTimeout(res, 1200));
      }
    };
    poll();
    try {
      const r = await api.post("/tally/ob/commit", {
        parsed_token: preview.parsed_token, as_of: asOf,
        overrides: payload, commit_id,
      });
      setResult(r.data);
      toast.success(`OB JV posted · ${r.data.voucher_number}${(r.data.created_accounts||[]).length ? ` · ${r.data.created_accounts.length} new account(s) created` : ""}`);
      await loadAccounts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Commit failed"); }
    stopPolling = true;
    setObProgress(null);
    setCommitting(false);
  };

  const setRow = (lc, patch) => setOverrides(prev => ({ ...prev, [lc]: { ...(prev[lc] || {}), ...patch } }));

  const unmatched = preview?.unmatched || [];
  const [ambiguousOnly, setAmbiguousOnly] = React.useState(true);
  const ambiguousCount = unmatched.filter(u => (u.confidence === "low") || u.is_ambiguous).length;
  const visibleUnmatched = unmatched.filter(u => {
    if (!ambiguousOnly) return true;
    return u.confidence === "low" || u.is_ambiguous;
  });
  const decisions = { total: unmatched.length, create: 0, existing: 0, skip: 0, pending: 0 };
  for (const u of unmatched) {
    const ov = overrides[u.ledger.toLowerCase()] || {};
    if (ov.action === "create" && ov.account_type) decisions.create++;
    else if (ov.action === "existing" && ov.account_id) decisions.existing++;
    else if (ov.action === "skip") decisions.skip++;
    else decisions.pending++;
  }

  // Bulk toolbar helpers — operate on VISIBLE (ambiguous-filtered) rows only
  const allChecked = visibleUnmatched.length > 0 && visibleUnmatched.every(u => selected.has(u.ledger.toLowerCase()));
  const someChecked = !allChecked && visibleUnmatched.some(u => selected.has(u.ledger.toLowerCase()));
  const toggleAll = () => {
    const s = new Set(selected);
    if (allChecked) visibleUnmatched.forEach(u => s.delete(u.ledger.toLowerCase()));
    else visibleUnmatched.forEach(u => s.add(u.ledger.toLowerCase()));
    setSelected(s);
  };
  const toggleOne = (lc) => {
    const s = new Set(selected);
    s.has(lc) ? s.delete(lc) : s.add(lc);
    setSelected(s);
  };
  const applyBulk = () => {
    if (selected.size === 0) return toast.error("Select at least one row");
    if (!bulkRoot && !bulkSub) return toast.error("Pick a Root and/or Sch III sub-category to apply");
    if (bulkRoot && bulkSub) {
      const allowed = (categoryTree?.[bulkRoot] || []).map(c => c.code);
      if (!allowed.includes(bulkSub)) return toast.error(`'${bulkSub}' is not valid for '${bulkRoot}'`);
    }
    setOverrides(prev => {
      const next = { ...prev };
      for (const u of unmatched) {
        const lc = u.ledger.toLowerCase();
        if (!selected.has(lc)) continue;
        const cur = next[lc] || {};
        next[lc] = { ...cur, action: "create",
          ...(bulkRoot ? { account_type: bulkRoot, gl_subcategory: "" } : {}),
          ...(bulkSub ? { gl_subcategory: bulkSub } : {}),
        };
      }
      return next;
    });
    toast.success(`Applied to ${selected.size} row(s)`);
  };
  const bulkSubs = bulkRoot ? (categoryTree?.[bulkRoot] || []) : [];

  if (result) {
    return (
      <div className="card-sharp p-5 border-l-4 border-l-green-500" data-testid="ob-result">
        <div className="flex items-center gap-2 text-green-500 mb-3">
          <CheckCircle2 className="w-5 h-5" /><span className="font-semibold">Opening Balances Posted</span>
        </div>
        <div className="text-sm space-y-1">
          <div>Voucher #: <span className="font-mono">{result.voucher_number}</span></div>
          <div>JV date: <span className="font-mono">{result.jv_date}</span> (one day before as-of {result.as_of})</div>
          <div>Lines posted: <span className="font-mono">{result.lines_posted}</span></div>
          <div>Total: Dr <span className="font-mono text-emerald-500">₹{result.total_debit.toLocaleString("en-IN")}</span> · Cr <span className="font-mono text-red-500">₹{result.total_credit.toLocaleString("en-IN")}</span></div>
          {result.created_accounts?.length > 0 && (
            <div className="mt-2">
              <div className="text-emerald-500 text-xs font-semibold">Created {result.created_accounts.length} new account(s):</div>
              <ul className="text-xs mt-1 space-y-0.5" data-testid="ob-created-accounts">
                {result.created_accounts.map(a => (
                  <li key={a.id} className="font-mono">
                    · <span className="text-muted-foreground">{a.code}</span> {a.name} — {a.account_type} / {a.gl_subcategory}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.skipped?.length > 0 && <div className="text-orange-500 text-xs mt-2">Skipped: {result.skipped.join(", ")}</div>}
        </div>
        <Button variant="outline" onClick={() => { setResult(null); setPreview(null); setFile(null); setOverrides({}); setSelected(new Set()); }} className="mt-4">Upload another TB</Button>
      </div>
    );
  }

  return (
    <div className="card-sharp p-5 space-y-4" data-testid="ob-panel">
      <div className="text-sm text-muted-foreground">
        Upload a Tally <b>Trial Balance</b> in Excel/CSV format with columns <code>Ledger · Debit · Credit</code>.
        Unmatched ledgers can be <b>created on-the-fly</b> here with the same Root × Schedule III categorization used in Tally imports.
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>As-of Date</Label><Input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} className="mt-1" data-testid="ob-as-of" /></div>
        <div><Label>TB File (Excel/CSV)</Label><Input type="file" accept=".xlsx,.xls,.csv" onChange={e => setFile(e.target.files?.[0] || null)} className="mt-1" data-testid="ob-file" /></div>
      </div>
      {!preview && <Button onClick={uploadFile} disabled={busy || !file} size="lg" data-testid="ob-preview-btn">{busy ? "Parsing…" : "Preview matches"}</Button>}
      {preview && (
        <div className="border-t pt-4 space-y-3" data-testid="ob-preview">
          <div className="grid grid-cols-4 gap-3 text-sm">
            <div className="border p-2"><div className="text-[10px] font-mono uppercase text-muted-foreground">Rows</div><div className="font-mono">{preview.row_count}</div></div>
            <div className="border p-2"><div className="text-[10px] font-mono uppercase text-muted-foreground">Matched</div><div className="font-mono text-emerald-500">{preview.matched.length}</div></div>
            <div className="border p-2"><div className="text-[10px] font-mono uppercase text-muted-foreground">Unmatched</div><div className={`font-mono ${preview.unmatched.length ? "text-orange-500" : ""}`}>{preview.unmatched.length}</div></div>
            <div className="border p-2"><div className="text-[10px] font-mono uppercase text-muted-foreground">Imbalance</div><div className={`font-mono ${Math.abs(preview.imbalance) > 0.005 ? "text-red-500" : "text-emerald-500"}`}>₹{preview.imbalance.toLocaleString("en-IN")}</div></div>
          </div>

          {unmatched.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs font-semibold text-orange-500">
                  {unmatched.length} unmatched · {decisions.create} will be created · {decisions.existing} mapped · {decisions.skip} skipped
                  {decisions.pending > 0 && <span className="text-red-500 ml-2">({decisions.pending} pending decision)</span>}
                </div>
                <label className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none px-2 py-1 border border-amber-500/40 bg-amber-500/5" data-testid="ob-ambiguous-only-wrap">
                  <input type="checkbox" checked={ambiguousOnly} onChange={e => setAmbiguousOnly(e.target.checked)}
                    data-testid="ob-ambiguous-only" />
                  <span>Show only ambiguous ({ambiguousCount})</span>
                </label>
              </div>
              <div className="text-[10px] text-muted-foreground" data-testid="ob-cat-legend">
                <span className="text-emerald-500">✓ {preview.confidence_stats?.high || 0} high</span>
                <span className="ml-2 text-blue-500">· {preview.confidence_stats?.medium || 0} medium (keyword)</span>
                <span className="ml-2 text-amber-500">· {preview.confidence_stats?.low || 0} need review</span>
                <span className="ml-3 italic">Backend uses ledger-name keywords (bank, sundry debtors, GST payable, …) to auto-suggest Root × Sch III category. Ambiguous rows fall back to <b>expense · other_expense</b> and need your review.</span>
              </div>

              {selected.size > 0 && (
                <div className="flex flex-wrap items-center gap-2 p-2 border border-accent/40 bg-accent/5" data-testid="ob-bulk-toolbar">
                  <span className="text-[11px] font-mono uppercase text-accent">{selected.size} selected</span>
                  <span className="text-[10px] text-muted-foreground">— set for all (as Create new):</span>
                  <Select value={bulkRoot} onValueChange={(v) => { setBulkRoot(v); setBulkSub(""); }}>
                    <SelectTrigger className="w-[130px] h-7 text-xs rounded-sm" data-testid="ob-bulk-root"><SelectValue placeholder="Root" /></SelectTrigger>
                    <SelectContent>{ROOT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={bulkSub} onValueChange={setBulkSub} disabled={!bulkRoot}>
                    <SelectTrigger className="w-[220px] h-7 text-xs rounded-sm" data-testid="ob-bulk-sub"><SelectValue placeholder={bulkRoot ? "Sch III sub-category" : "Pick root first"} /></SelectTrigger>
                    <SelectContent className="max-h-[280px]">{bulkSubs.map(s => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" onClick={applyBulk} data-testid="ob-bulk-apply">Apply</Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="ob-bulk-clear">Clear</Button>
                </div>
              )}

              <div className="overflow-x-auto max-h-[420px] overflow-y-auto border border-border">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-card z-10">
                    <tr className="border-b">
                      <th className="w-[28px] py-2 px-2">
                        <input type="checkbox" checked={allChecked}
                          ref={el => { if (el) el.indeterminate = someChecked; }}
                          onChange={toggleAll} data-testid="ob-check-all" aria-label="Select all unmatched" />
                      </th>
                      <th className="text-left uppercase text-[10px]">Ledger (from TB)</th>
                      <th className="text-left uppercase text-[10px]">Confidence</th>
                      <th className="text-right uppercase text-[10px]">Dr</th>
                      <th className="text-right uppercase text-[10px]">Cr</th>
                      <th className="text-left uppercase text-[10px]">Action</th>
                      <th className="text-left uppercase text-[10px]">Root / Existing</th>
                      <th className="text-left uppercase text-[10px]">Sch III sub-category</th>
                      <th className="text-left uppercase text-[10px]">Code</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleUnmatched.map((u, i) => {
                      const lc = u.ledger.toLowerCase();
                      const ov = overrides[lc] || { action: "create" };
                      const action = ov.action || "create";
                      const subsForRoot = categoryTree?.[ov.account_type] || [];
                      return (
                        <tr key={i} className={`border-b ${action === "skip" ? "opacity-40" : ""}`} data-testid={`ob-row-${i}`}>
                          <td className="px-2">
                            <input type="checkbox" checked={selected.has(lc)} onChange={() => toggleOne(lc)}
                              data-testid={`ob-check-${i}`} aria-label={`Select ${u.ledger}`} />
                          </td>
                          <td className="py-1.5 px-2 font-medium">{u.ledger}</td>
                          <td>
                            <ConfidencePill level={u.confidence || (u.is_ambiguous ? "low" : "medium")} />
                          </td>
                          <td className="text-right font-mono">{fmtINR(u.debit)}</td>
                          <td className="text-right font-mono">{fmtINR(u.credit)}</td>
                          <td>
                            <Select value={action} onValueChange={(v) => setRow(lc, { action: v })}>
                              <SelectTrigger className="text-xs h-7 rounded-sm w-[120px]" data-testid={`ob-action-${i}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="create">Create new</SelectItem>
                                <SelectItem value="existing">Map existing</SelectItem>
                                <SelectItem value="skip">Skip</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td>
                            {action === "create" && (
                              <Select value={ov.account_type || ""} onValueChange={(v) => setRow(lc, { account_type: v, gl_subcategory: "" })}>
                                <SelectTrigger className="text-xs h-7 rounded-sm min-w-[110px]" data-testid={`ob-root-${i}`}>
                                  <SelectValue placeholder="Root" />
                                </SelectTrigger>
                                <SelectContent>{ROOT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                              </Select>
                            )}
                            {action === "existing" && (
                              <Select value={ov.account_id || ""} onValueChange={(v) => setRow(lc, { account_id: v })}>
                                <SelectTrigger className="text-xs h-7 rounded-sm min-w-[180px]" data-testid={`ob-existing-${i}`}>
                                  <SelectValue placeholder="Choose account…" />
                                </SelectTrigger>
                                <SelectContent className="max-h-[280px]">
                                  {existingAccounts.map(a => <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            )}
                            {action === "skip" && <span className="text-[10px] text-muted-foreground italic">—</span>}
                          </td>
                          <td>
                            {action === "create" && (
                              <Select value={ov.gl_subcategory || ""} onValueChange={(v) => setRow(lc, { gl_subcategory: v })} disabled={!ov.account_type}>
                                <SelectTrigger className="text-xs h-7 rounded-sm min-w-[200px]" data-testid={`ob-sub-${i}`}>
                                  <SelectValue placeholder={ov.account_type ? "Auto-derive" : "Pick Root first"} />
                                </SelectTrigger>
                                <SelectContent className="max-h-[280px]">
                                  {subsForRoot.map(s => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            )}
                            {action !== "create" && <span className="text-[10px] text-muted-foreground italic">—</span>}
                          </td>
                          <td>
                            {action === "create" && (
                              <Input value={ov.code || ""} onChange={e => setRow(lc, { code: e.target.value })}
                                placeholder="auto" className="h-7 text-xs w-[80px]" data-testid={`ob-code-${i}`} />
                            )}
                            {action !== "create" && <span className="text-[10px] text-muted-foreground italic">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Live per-row progress cursor — polled while the commit request is in flight */}
          {obProgress && committing && (
            <div className="card-sharp p-3 border-l-4 border-l-accent bg-accent/5" data-testid="ob-progress">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-xs font-medium">
                  <span className="text-accent">{obProgress.stage || "Posting…"}</span>
                  {obProgress.current_account && (
                    <span className="ml-2 font-mono text-muted-foreground">· {obProgress.current_account}</span>
                  )}
                </div>
                <div className="text-xs font-mono font-bold text-accent">{obProgress.pct || 0}%</div>
              </div>
              <div className="w-full h-1.5 bg-white/10 overflow-hidden">
                <div className="h-full bg-accent transition-all"
                  style={{ width: `${obProgress.pct || 0}%` }} />
              </div>
              <div className="text-[10px] text-muted-foreground mt-1 font-mono">
                Row {obProgress.done || 0} / {obProgress.total || 0}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button onClick={commit} disabled={committing || (preview.matched.length + decisions.create + decisions.existing === 0)}
              data-testid="ob-commit-btn">
              {committing ? "Posting…" : `Post OB JV (${preview.matched.length + decisions.create + decisions.existing} ledgers${decisions.create ? `, +${decisions.create} new accounts` : ""})`}
            </Button>
            <Button variant="outline" onClick={() => { setPreview(null); setFile(null); setOverrides({}); setSelected(new Set()); }}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Heuristic — guesses the Root from a Tally ledger name (frontend hint only)
function _guessRoot(name) {
  const n = (name || "").toLowerCase();
  if (/bank|cash|debtor|receivable|inventory|stock|input.*gst|prepaid|deposit/.test(n)) return "asset";
  if (/creditor|payable|loan|gst payable|tds payable|provision/.test(n)) return "liability";
  if (/capital|reserve|retained|surplus/.test(n)) return "equity";
  if (/sales|revenue|service income|interest received|rent received|other income/.test(n)) return "income";
  return "expense";
}

export default function TallyMigrator() {
  const [file, setFile] = useState(null);
  const [source, setSource] = useState("xml");
  const [gatewayUrl, setGatewayUrl] = useState("http://localhost:9000");
  const [companyName, setCompanyName] = useState("");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(""); // "" | "masters" | "transactions" | "both"
  const [ack, setAck] = useState(false);
  const [ledgerOverrides, setLedgerOverrides] = useState({});   // {name_lc: {category, map_to_existing_id, skip, account_type, gl_subcategory}}
  const [existingMasters, setExistingMasters] = useState({ accounts: [], parties: [] });
  const [categoryTree, setCategoryTree] = useState({});
  const [batches, setBatches] = useState([]);
  const [options, setOptions] = useState({
    import_opening_balances: true,
    auto_create_unmatched: true,
    post_opening_jv: true,
  });
  // Iter-195 · Persisted voucher-type map (last accountant-approved import)
  const [vtypeMapPersistedAt, setVtypeMapPersistedAt] = useState(null);

  // Auto-load the accountant's last-saved Tally mapping.
  React.useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/migrator/voucher-type-map/tally");
        if (r.data?.map && Object.keys(r.data.map).length) {
          setOptions((o) => ({ ...o, voucher_type_map: r.data.map }));
          setVtypeMapPersistedAt(r.data.updated_at);
        }
      } catch { /* first-time — silent */ }
    })();
  }, []);

  const loadBatches = async () => {
    try { setBatches((await api.get("/tally/batches")).data || []); } catch(e) {}
  };
  const loadExistingMasters = async () => {
    try { setExistingMasters((await api.get("/tally/masters/existing")).data); } catch(e) {}
  };
  const loadCategoryTree = async () => {
    try { setCategoryTree((await api.get("/accounts/categories")).data?.tree || {}); } catch(e) {}
  };
  React.useEffect(() => { loadBatches(); loadExistingMasters(); loadCategoryTree(); }, []);

  const [uploadPct, setUploadPct] = useState(0);
  const [uploadStage, setUploadStage] = useState("");
  const [commitProgress, setCommitProgress] = useState(null);   // {pct, stage, note}

  const handleUpload = async () => {
    if (!file) return toast.error("Choose a file first");
    // Guard: 200 MB hard cap. Anything larger is almost never a real Tally
    // export and just churns memory on the ingress.
    if (file.size > SIZE_THRESHOLDS.hard_cap) {
      return toast.error(`File is ${(file.size / 1048576).toFixed(1)} MB — max is 200 MB. Split the export in Tally by financial year and retry.`);
    }
    // Soft-warn confirmation for anything above the 15 MB green zone. Users
    // can still proceed but we make them aware their upload may be slower
    // (or fail on flaky connections).
    const status = _sizeStatus(file.size);
    if (status === "red") {
      const ok = window.confirm(
        `This file is ${(file.size / 1048576).toFixed(1)} MB. Files above 30 MB frequently fail on our servers.\n\n` +
        `We strongly recommend exporting from Tally one financial year at a time.\n\n` +
        `Continue with this file anyway?`);
      if (!ok) return;
    }
    setUploading(true); setUploadPct(0); setUploadStage("Preparing upload…");

    // Small helper — retry a chunk up to 3× with exponential backoff so a
    // single dropped packet on a corporate firewall doesn't kill the entire
    // multi-minute upload.
    const uploadChunkWithRetry = async (uploadId, seq, data_b64) => {
      let lastErr = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          await api.post(`/tally/upload/${uploadId}/chunk`, { seq, data_b64 }, { timeout: 90000 });
          return;
        } catch (e) {
          lastErr = e;
          const status = e?.response?.status;
          e._tallyStage = `chunk ${seq}`;
          if (status === 401 || status === 403) throw e;
          if (attempt < 3) await new Promise((r) => setTimeout(r, 800 * attempt));
        }
      }
      throw lastErr;
    };

    try {
      // ------------------------------------------------------------------
      // Chunked upload — bypasses the K8s ingress + Cloudflare 1 MB request
      // ceiling that used to 520-out on real 10-100 MB Tally exports.
      // Chunks are 256 KB (was 512 KB) — smaller = friendlier on flaky
      // corporate WiFi and doesn't trip over Fortinet ICAP scanners that
      // buffer request bodies.
      // ------------------------------------------------------------------
      let stage = "init";
      const initR = await api.post("/tally/upload/init", {
        filename: file.name, source, total_bytes: file.size,
      }).catch((e) => { e._tallyStage = "init"; throw e; });
      const uploadId = initR.data.upload_id;
      const chunkSize = Math.min(initR.data.chunk_size || 256 * 1024, 256 * 1024);
      const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));
      setUploadStage(`Uploading ${totalChunks} chunk(s) · ${(file.size / 1048576).toFixed(1)} MB…`);
      for (let i = 0; i < totalChunks; i++) {
        stage = `chunk ${i + 1}/${totalChunks}`;
        const blob = file.slice(i * chunkSize, (i + 1) * chunkSize);
        const buf = await blob.arrayBuffer();
        const bytes = new Uint8Array(buf);
        let bin = "";
        const step = 0x8000;
        for (let j = 0; j < bytes.length; j += step) {
          bin += String.fromCharCode.apply(null, bytes.subarray(j, j + step));
        }
        const data_b64 = btoa(bin);
        await uploadChunkWithRetry(uploadId, i, data_b64);
        setUploadPct(Math.round(((i + 1) / totalChunks) * 92));
      }
      stage = "finalize (parsing)";
      setUploadStage("Parsing on server…");
      setUploadPct(95);
      // Finalize can take a while for big files (parser + master lookups).
      // Give it 180s and surface a clearer error on failure.
      const finalR = await api.post(`/tally/upload/${uploadId}/finalize`, {}, { timeout: 180000 })
        .catch((e) => { e._tallyStage = "finalize"; throw e; });
      setUploadPct(100);
      const data = finalR.data;
      data.ledgers_mapping = data.ledgers_mapping_preview || [];
      setPreview(data);
      await loadExistingMasters();
      toast.success(`Preview ready · ${data.ledgers_mapping_total || 0} ledgers detected`);
      // Backend returns first N rows only; page the rest in from
      // /tally/mappings/{token} in the background so the UI stays snappy.
      const total = data.ledgers_mapping_total || data.ledgers_mapping.length;
      if (total > data.ledgers_mapping.length) {
        (async () => {
          let acc = data.ledgers_mapping.slice();
          try {
            while (acc.length < total) {
              const r = await api.get(`/tally/mappings/${data.parsed_token}`, {
                params: { offset: acc.length, limit: 500 },
              });
              const rows = r.data?.rows || [];
              if (!rows.length) break;
              acc = acc.concat(rows);
              setPreview((p) => p ? { ...p, ledgers_mapping: acc } : p);
            }
          } catch {
            toast.error(`Could not load full ledger mapping — showing first ${acc.length} of ${total} rows`);
          }
        })();
      }
    } catch (e) {
      // Surface the actual error + which upload phase failed so screenshots
      // are actually useful to support.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const stage = e?._tallyStage || "upload";
      let msg;
      if (status === 520 || status === 502 || status === 504) {
        msg = `Tally ${stage} failed (${status}) — the server crashed processing your file. If it's >50 MB, split it by financial year in Tally and try again. If not, contact support with this exact message.`;
      } else if (status === 507) {
        msg = `Tally ${stage} out-of-memory (507): ${detail || "server ran out of memory"} — export smaller FY-wise files from Tally.`;
      } else if (detail) {
        msg = `Tally ${stage} failed (${status}): ${detail}`;
      } else if (e?.code === "ECONNABORTED") {
        msg = `Tally ${stage} timed out — try a smaller file or check your network.`;
      } else {
        msg = `Tally ${stage} failed: ${e?.message || e}`;
      }
      toast.error(msg, { duration: 15000 });
      // eslint-disable-next-line no-console
      console.error("[Tally upload] failed", { stage, status, detail, error: e });
    }
    setUploading(false); setUploadPct(0); setUploadStage("");
  };

  const downloadPreviewPDF = async () => {
    if (!preview?.parsed_token) return;
    try {
      const r = await api.post("/tally/preview-pdf", { parsed_token: preview.parsed_token }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `tally_migration_preview_${(preview.company?.name || "company").replace(/\s+/g, "_")}.pdf`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Preview PDF ready — share with your CA");
    } catch (e) { toast.error(e.response?.data?.detail || String(e)); }
  };

  const handleGatewaySync = async () => {
    if (!gatewayUrl || !companyName) return toast.error("Gateway URL + company name required");
    setUploading(true);
    try {
      const r = await api.post("/tally/gateway/preview", { gateway_url: gatewayUrl, company_name: companyName });
      setPreview(r.data);
      await loadExistingMasters();
      toast.success("Fetched from gateway");
    } catch (e) { toast.error(e.response?.data?.detail || String(e)); }
    setUploading(false);
  };

  const handleCommit = async (stage) => {
    setCommitting(stage);
    setCommitProgress({ pct: 0, stage: "starting", note: "Submitting commit…" });
    // Poll progress every 1s while the commit is in flight
    let pollTimer = null;
    let ownBatchId = null;
    const startPolling = (bid) => {
      ownBatchId = bid;
      pollTimer = setInterval(async () => {
        try {
          const p = await api.get(`/tally/progress/${bid}`);
          setCommitProgress({ pct: p.data.pct || 0, stage: p.data.stage || "", note: p.data.note || "" });
          if (p.data.finished) { clearInterval(pollTimer); pollTimer = null; }
        } catch { /* poll survives transient errors */ }
      }, 1200);
    };
    try {
      const finalOptions = { ...options, overrides: { ledgers: ledgerOverrides } };
      if (preview.company_verification?.identity_match === "mismatch") {
        finalOptions.allow_gstin_mismatch = true;
      }
      const path = stage === "masters" ? "/tally/commit/masters"
                    : stage === "transactions" ? "/tally/commit/transactions"
                    : "/tally/commit";
      // Kick off polling shortly after commit starts (backend allocates batch_id first)
      setTimeout(async () => {
        // Grab the newest in-progress batch for this org from tally_progress via a
        // best-effort lookup — but simpler: after commit resolves, use r.data.batch_id
      }, 200);
      const r = await api.post(path, { parsed_token: preview.parsed_token, options: finalOptions });
      if (pollTimer) clearInterval(pollTimer);
      setCommitProgress({ pct: 100, stage: "done", note: "Commit complete" });
      setResult(r.data);
      await loadBatches();
      toast.success(`Stage "${stage}" committed · batch ${(r.data?.batch_id || "").slice(0,8)}`);
      // Iter-195 · Persist the voucher-type map so the next Tally import auto-loads.
      const vt = finalOptions.voucher_type_map;
      if (vt && Object.keys(vt).length) {
        try {
          await api.put("/migrator/voucher-type-map/tally", { map: vt });
          setVtypeMapPersistedAt(new Date().toISOString());
        } catch { /* non-fatal */ }
      }
      setTimeout(() => setCommitProgress(null), 800);
    } catch (e) {
      if (pollTimer) clearInterval(pollTimer);
      setCommitProgress(null);
      toast.error(e.response?.data?.detail || String(e));
    }
    setCommitting("");
  };

  const reset = () => { setFile(null); setPreview(null); setResult(null); setAck(false); setLedgerOverrides({}); };

  const eraseBatch = async (b) => {
    if (!window.confirm(`Erase this import? Marks ${b.counts?.accounts || 0} accounts, ${b.counts?.parties || 0} parties, ${b.counts?.vouchers || 0} vouchers inactive (audit trail preserved).`)) return;
    try {
      const r = await api.delete(`/tally/batches/${b.id}`);
      toast.success(`Erased: ${JSON.stringify(r.data.erased)}`);
      loadBatches();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erase failed"); }
  };

  const downloadBatchReport = (b) =>
    downloadBlob(`/tally/batches/${b.id}/report.xlsx`, `tally_migration_report_${b.id.slice(0,8)}.xlsx`);

  const downloadHealthReport = (b) =>
    downloadBlob(`/tally/batches/${b.id}/health-report.pdf`, `tally_health_report_${b.id.slice(0,8)}.pdf`);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="tally-migrator-page">
      <PageHeader eyebrow="Data Migration" title="Tally Prime → Zenith 360"
        description="Two-stage import — masters first (with category mapping), then transactions." />

      {/* Commit progress bar — visible only while a commit is running */}
      {commitProgress && (
        <div className="card-sharp p-4 border-l-4 border-l-accent" data-testid="commit-progress">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-sm font-medium">
              Committing… <span className="text-xs font-mono text-muted-foreground">{commitProgress.stage}</span>
            </div>
            <div className="text-sm font-mono font-bold text-accent">{commitProgress.pct}%</div>
          </div>
          <div className="h-2.5 bg-muted rounded-sm overflow-hidden">
            <div className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all"
                 style={{ width: `${commitProgress.pct}%` }} />
          </div>
          {commitProgress.note && <div className="text-[11px] text-muted-foreground mt-1.5">{commitProgress.note}</div>}
        </div>
      )}

      {result ? (
        <ResultPanel result={result} batchId={result.batch_id} onReset={reset}
                       onErase={() => eraseBatch({ id: result.batch_id, counts: result })} />
      ) : preview ? (
        <PreviewPanel
          preview={preview}
          options={options} setOptions={setOptions}
          ack={ack} setAck={setAck}
          ledgerOverrides={ledgerOverrides} setLedgerOverrides={setLedgerOverrides}
          existingMasters={existingMasters}
          categoryTree={categoryTree}
          onCommitStage={handleCommit}
          committing={committing}
          onSynced={handleUpload}
          onDownloadPreviewPDF={downloadPreviewPDF}
          vtypeMapPersistedAt={vtypeMapPersistedAt}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Tabs defaultValue="upload">
              <TabsList className="rounded-sm">
                <TabsTrigger value="upload" className="rounded-sm" data-testid="tab-upload"><UploadCloud className="w-4 h-4 mr-2" /> Upload File</TabsTrigger>
                <TabsTrigger value="gateway" className="rounded-sm" data-testid="tab-gateway"><Server className="w-4 h-4 mr-2" /> Tally HTTP Gateway</TabsTrigger>
                <TabsTrigger value="ob" className="rounded-sm" data-testid="tab-ob"><FileText className="w-4 h-4 mr-2" /> Import TB (Opening Balances)</TabsTrigger>
                <TabsTrigger value="review" className="rounded-sm" data-testid="tab-review"><ListChecks className="w-4 h-4 mr-2" /> Review Categorization</TabsTrigger>
              </TabsList>
              <TabsContent value="upload" className="mt-4">
                <div className="card-sharp p-5 space-y-4" data-testid="upload-panel">
                  <FileSizeBanner />
                  <div>
                    <Label>File format</Label>
                    <Select value={source} onValueChange={setSource}>
                      <SelectTrigger className="rounded-sm mt-1" data-testid="src-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="xml">Tally XML (.xml) — recommended</SelectItem>
                        <SelectItem value="excel_ledgers">Excel — Ledgers only</SelectItem>
                        <SelectItem value="excel_stock">Excel — Stock Items only</SelectItem>
                        <SelectItem value="excel_vouchers">Excel — Vouchers only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Choose Tally export file</Label>
                    <Input type="file" accept={source === "xml" ? ".xml" : ".xlsx,.xls,.csv"} onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1" data-testid="file-input" />
                    {file && (
                      <div className="text-xs text-muted-foreground mt-2 flex items-center gap-2 flex-wrap">
                        <FileText className="w-3 h-3 inline" />
                        <span>{file.name}</span>
                        <SizePill bytes={file.size} />
                      </div>
                    )}
                  </div>
                  <Button onClick={handleUpload} disabled={uploading || !file} size="lg" data-testid="preview-btn">
                    {uploading ? "Parsing…" : "Preview Migration"}
                  </Button>
                  {uploading && uploadPct > 0 && (
                    <div className="mt-3" data-testid="upload-progress">
                      <div className="text-xs font-mono text-muted-foreground mb-1">Uploading… {uploadPct}%</div>
                      <div className="h-2 bg-muted rounded-sm overflow-hidden">
                        <div className="h-full bg-accent transition-all" style={{ width: `${uploadPct}%` }} />
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
              <TabsContent value="gateway" className="mt-4">
                <div className="card-sharp p-5 space-y-4" data-testid="gateway-panel">
                  <div className="text-sm text-muted-foreground">Pull directly from a running Tally instance. Enable <b>F1 → Configure → TallyPrime acts as → Both</b>.</div>
                  <div><Label>Gateway URL</Label><Input value={gatewayUrl} onChange={(e) => setGatewayUrl(e.target.value)} placeholder="http://tally-machine:9000" className="mt-1" data-testid="gateway-url" /></div>
                  <div><Label>Company Name (as in Tally)</Label><Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="e.g. Acme Corp" className="mt-1" data-testid="gateway-company" /></div>
                  <Button onClick={handleGatewaySync} disabled={uploading} size="lg" data-testid="gateway-btn">{uploading ? "Fetching…" : "Fetch & Preview"}</Button>
                </div>
              </TabsContent>
              <TabsContent value="ob" className="mt-4">
                <OpeningBalanceUploadPane categoryTree={categoryTree} />
              </TabsContent>
              <TabsContent value="review" className="mt-4">
                <ReviewCategorizationPane categoryTree={categoryTree} />
              </TabsContent>
            </Tabs>
          </div>
          <div><HelpDrawer /></div>
        </div>
      )}

      {!preview && !result && batches.length > 0 && (
        <div className="card-sharp p-5" data-testid="past-imports">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Past Imports · Download report or erase</div>
            <div className="text-[11px] text-muted-foreground">Erase soft-deletes every account · party · item · voucher · segment · dimension created by that batch. Audit trail preserved.</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Date</th>
                  <th className="text-left">Source Company</th>
                  <th className="text-left">GSTIN</th>
                  <th className="text-left">Stage</th>
                  <th className="text-right">Accts</th>
                  <th className="text-right">Parties</th>
                  <th className="text-right">Vouchers</th>
                  <th className="text-right">Tx ✓/⚠/✗</th>
                  <th className="text-center">Status</th>
                  <th className="text-right"></th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.id} className={`border-b ${b.status === "erased" ? "opacity-50" : ""}`} data-testid={`batch-${b.id.slice(0,8)}`}>
                    <td className="py-2 text-xs">{b.created_at?.slice(0,16).replace("T", " ")}</td>
                    <td>{b.source_company_name || "—"}</td>
                    <td className="font-mono text-xs">{b.source_company_gstin || "—"}</td>
                    <td className="text-[10px] font-mono uppercase">{b.stage || "both"}</td>
                    <td className="text-right font-mono">{b.counts?.accounts || 0}</td>
                    <td className="text-right font-mono">{b.counts?.parties || 0}</td>
                    <td className="text-right font-mono">{b.counts?.vouchers || 0}</td>
                    <td className="text-right font-mono text-xs">
                      <span className="text-green-500">{b.tx_summary?.success || 0}</span>
                      <span className="text-muted-foreground">/</span>
                      <span className="text-orange-500">{b.tx_summary?.skipped || 0}</span>
                      <span className="text-muted-foreground">/</span>
                      <span className="text-red-500">{b.tx_summary?.failed || 0}</span>
                    </td>
                    <td className="text-center">
                      <span className={`text-[10px] font-mono uppercase px-2 py-0.5 border ${b.status === "erased" ? "text-muted-foreground border-muted-foreground" : "text-green-500 border-green-500"}`}>{b.status}</span>
                    </td>
                    <td className="text-right space-x-2 whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => downloadBatchReport(b)} data-testid={`report-batch-${b.id.slice(0,8)}`}><Download className="w-3 h-3 mr-1" />Excel</Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadHealthReport(b)} data-testid={`health-batch-${b.id.slice(0,8)}`}><Download className="w-3 h-3 mr-1" />Health PDF</Button>
                      {b.status !== "erased" && (
                        <Button size="sm" variant="outline" onClick={() => eraseBatch(b)} data-testid={`erase-batch-${b.id.slice(0,8)}`}>Erase</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
